import { useState, useEffect } from "react";
import { Sparkles, Loader2, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";

export default function MarketCompetitorIntel({ city, state, propertyName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasAttempted, setHasAttempted] = useState(false);

  const generate = async (refresh = false) => {
    if (!city?.trim()) {
      setError("Enter the city in Property details first.");
      return;
    }
    setLoading(true); setError(null); setHasAttempted(true);
    try {
      const { data: r } = await api.post("/property-ic/local-intel", {
        city: city.trim(), state: state?.trim() || "", property_name: propertyName?.trim() || "",
        refresh,
      });
      setData(r);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset cached panel when city changes
  useEffect(() => { setData(null); setError(null); setHasAttempted(false); }, [city, state]);

  const cachedLabel = data?.from_cache && data?.generated_at
    ? `cached · ${new Date(data.generated_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`
    : data?.generated_at ? `fresh · ${new Date(data.generated_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : null;

  return (
    <div className="rounded-lg border border-purple-300 bg-purple-50/30 p-3" data-testid="ic-market-intel">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Sparkles className="w-4 h-4 text-purple-700" />
        <span className="font-display font-bold text-[13px] text-purple-900">Market & competitor intelligence</span>
        <span className="text-[10px] text-purple-700">
          {city ? `for ${city}${state ? ", " + state : ""}` : "enter a city above"} · 10 bullets · powered by Claude
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {cachedLabel && <span className="text-[9.5px] text-purple-600">{cachedLabel}</span>}
          <button
            onClick={() => generate(!!data)}
            disabled={loading || !city?.trim()}
            data-testid="ic-intel-generate"
            className="text-[10.5px] font-medium px-2.5 py-1 rounded bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-50 inline-flex items-center gap-1 pointer-events-auto"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {data ? "Refresh" : "Generate"}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 my-2" data-testid="ic-intel-error">
          {error}
        </div>
      )}

      {!data && !loading && !hasAttempted && (
        <div className="text-[11px] text-purple-800/70 py-3 text-center bg-white/60 border border-dashed border-purple-200 rounded">
          Click <b>Generate</b> to pull 10 bullets on hostel/hotel supply, ADR, occupancy & competitors specific to <b>{city || "this city"}</b>.
        </div>
      )}

      {loading && (
        <div className="text-[11px] text-purple-700/80 py-4 text-center inline-flex items-center justify-center gap-2 w-full">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating market intel for {city}…
        </div>
      )}

      {data && (
        <div className="space-y-3">
          {/* 10 market bullets */}
          <div>
            <div className="text-[9.5px] uppercase font-bold text-purple-700 tracking-wide mb-1.5">Market signals · {data.location_label}</div>
            <ol className="space-y-1.5" data-testid="ic-intel-market">
              {(data.market || []).map((b, i) => (
                <li key={`${i}-${(b || "").slice(0, 20)}`} className="bg-white border border-purple-200 rounded px-2 py-1.5 text-[11.5px] text-zinc-800 leading-snug flex gap-2" data-testid={`ic-intel-bullet-${i}`}>
                  <span className="font-display font-black text-purple-700 text-[11px] mt-0.5 flex-shrink-0 w-4 tabular-nums">{i + 1}.</span>
                  <span className="flex-1">{b}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Competitors */}
          {data.competitors?.length > 0 && (
            <div>
              <div className="text-[9.5px] uppercase font-bold text-purple-700 tracking-wide mb-1.5">Local competitors</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="ic-intel-competitors">
                {data.competitors.map((c, i) => (
                  <div key={c.name || `comp-${i}`} className="bg-white border border-purple-200 rounded p-2 text-[11px]" data-testid={`ic-intel-comp-${i}`}>
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="font-display font-bold text-zinc-900 text-[11.5px] truncate">{c.name}</span>
                      <span className="text-[9.5px] uppercase tracking-wide text-purple-700 font-bold">{c.type || "—"}</span>
                    </div>
                    <div className="text-[10.5px] text-zinc-600 tabular-nums">
                      {c.est_adr_inr ? `₹${c.est_adr_inr} ADR` : "ADR —"}
                      {c.est_occ_pct ? ` · ${c.est_occ_pct}% occ` : ""}
                    </div>
                    {c.notes && <p className="text-[10px] text-zinc-500 mt-0.5 italic leading-snug">{c.notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-[9.5px] text-purple-600 italic">
            ⚠ AI-generated estimates. Cross-check critical numbers (ADR, occupancy) before committing.
          </div>
        </div>
      )}
    </div>
  );
}
